import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Plus,
  Search,
  Trash2,
  Edit3,
  FileText,
  BarChart3,
  Sparkles,
  FolderKanban,
  LayoutGrid,
  List,
  Pencil
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function TemplateManager() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProjectType, setFilterProjectType] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '', projectTypeId: '' });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchProjectTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/project-types', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjectTypes(data.filter(pt => pt.isActive));
        // Définir le premier type par défaut pour la création
        if (data.length > 0) {
          setNewTemplate(prev => ({ ...prev, projectTypeId: data[0].id }));
        }
      }
    } catch (err) {
      console.error('Fetch project types error:', err);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      let url = '/api/templates';
      if (filterProjectType) {
        url += `?projectTypeId=${filterProjectType}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Fetch templates error:', err);
    }
    setLoading(false);
  }, [filterProjectType]);

  useEffect(() => {
    fetchProjectTypes();
  }, [fetchProjectTypes]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.projectTypeId) return;

    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newTemplate)
      });

      if (response.ok) {
        const created = await response.json();
        setCreateDialogOpen(false);
        setNewTemplate({ name: '', description: '', projectTypeId: projectTypes[0]?.id || '' });
        navigate(`/admin/templates/${created.id}`);
      }
    } catch (err) {
      console.error('Create template error:', err);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/${template.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !template.isActive })
      });
      fetchTemplates();
    } catch (err) {
      console.error('Toggle active error:', err);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template définitivement ?')) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTemplates();
    } catch (err) {
      console.error('Delete template error:', err);
    }
  };

  const handleOpenEditDialog = (template) => {
    setEditingTemplate({ id: template.id, name: template.name, description: template.description || '' });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate?.name.trim()) return;

    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/templates/${editingTemplate.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: editingTemplate.name, description: editingTemplate.description })
      });
      setEditDialogOpen(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      console.error('Update template error:', err);
    }
  };

  const calculateTemplateStats = (template) => {
    let totalSections = template.sections?.length || 0;
    let totalItems = 0;
    let totalDefaultHours = 0;
    const categories = [];

    template.sections?.forEach(section => {
      // Collecter les catégories pour affichage
      if (section.category) {
        categories.push(section.category);
      } else if (section.customCategory) {
        categories.push({ name: section.customCategory, slug: 'custom' });
      }
      
      totalItems += section.items?.length || 0;
      section.items?.forEach(item => {
        // Calculer les heures dynamiques
        if (item.hours && item.hours.length > 0) {
          totalDefaultHours += item.hours.reduce((sum, h) => sum + (h.defaultHours || 0), 0);
        }
      });
    });

    return { totalSections, totalItems, totalDefaultHours, categories };
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Chargement des templates...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Templates de devis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez vos modèles de devis réutilisables
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Sparkles size={18} />}
            onClick={() => navigate('/admin/templates/ai/new')}
            sx={{
              borderColor: 'rgba(168, 85, 247, 0.5)',
              color: '#a855f7',
              '&:hover': {
                borderColor: '#a855f7',
                bgcolor: 'rgba(168, 85, 247, 0.1)'
              }
            }}
          >
            Générer avec AI
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nouveau template
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Rechercher un template..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Type de projet</InputLabel>
            <Select
              value={filterProjectType}
              onChange={(e) => setFilterProjectType(e.target.value)}
              label="Type de projet"
            >
              <MenuItem value="">Tous les types</MenuItem>
              {projectTypes.map(pt => (
                <MenuItem key={pt.id} value={pt.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: pt.color }} />
                    {pt.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="grid">
            <Tooltip title="Vue grille">
              <LayoutGrid size={18} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list">
            <Tooltip title="Vue liste">
              <List size={18} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Templates Grid or List */}
      {filteredTemplates.length > 0 ? (
        viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {filteredTemplates.map((template) => {
              const stats = calculateTemplateStats(template);
              
              return (
                <Grid item xs={12} sm={6} lg={4} key={template.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: template.projectType?.color ? `${template.projectType.color}50` : 'rgba(0, 212, 170, 0.3)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            p: 1, 
                            borderRadius: 1, 
                            bgcolor: template.projectType?.color ? `${template.projectType.color}15` : 'rgba(0, 212, 170, 0.1)',
                            display: 'flex'
                          }}>
                            <FileText size={20} color={template.projectType?.color || '#00d4aa'} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {template.name}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                label={template.isActive ? 'Actif' : 'Inactif'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: template.isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                  color: template.isActive ? '#10b981' : 'text.secondary'
                                }}
                              />
                            </Stack>
                          </Box>
                        </Box>
                      </Box>

                      {/* Project Type Badge */}
                      {template.projectType && (
                        <Chip
                          icon={<FolderKanban size={12} />}
                          label={template.projectType.name}
                          size="small"
                          sx={{
                            mb: 2,
                            bgcolor: `${template.projectType.color}15`,
                            color: template.projectType.color,
                            borderColor: `${template.projectType.color}30`,
                            border: '1px solid',
                            '& .MuiChip-icon': { color: template.projectType.color }
                          }}
                        />
                      )}

                      {/* Description */}
                      {template.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {template.description}
                        </Typography>
                      )}

                      {/* Stats */}
                      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Sections</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{stats.totalSections}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Éléments</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{stats.totalItems}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Heures</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{stats.totalDefaultHours}h</Typography>
                        </Box>
                      </Stack>

                      {/* Categories */}
                      {stats.categories.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                          {stats.categories.slice(0, 4).map((cat, idx) => (
                            <Tooltip key={idx} title={cat.name}>
                              <Chip
                                label={cat.name}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.6rem',
                                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                                  color: 'text.secondary'
                                }}
                              />
                            </Tooltip>
                          ))}
                          {stats.categories.length > 4 && (
                            <Chip
                              label={`+${stats.categories.length - 4}`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.6rem',
                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                                color: 'text.secondary'
                              }}
                            />
                          )}
                        </Stack>
                      )}

                      {/* Usage */}
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BarChart3 size={14} color="#6b7280" />
                        <Typography variant="caption" color="text.secondary">
                          Utilisé {template.usageCount || 0} fois
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={template.isActive}
                            onChange={() => handleToggleActive(template)}
                          />
                        }
                        label={<Typography variant="caption">Actif</Typography>}
                      />
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Renommer">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(template)}>
                            <Pencil size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier contenu">
                          <IconButton size="small" onClick={() => navigate(`/admin/templates/${template.id}`)}>
                            <Edit3 size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" onClick={() => handleDeleteTemplate(template.id)} sx={{ color: '#ef4444' }}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          /* List View */
          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Sections</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Items</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Heures</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Utilisations</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Statut</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.map((template) => {
                  const stats = calculateTemplateStats(template);
                  return (
                    <TableRow 
                      key={template.id}
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' },
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ 
                            p: 0.75, 
                            borderRadius: 1, 
                            bgcolor: template.projectType?.color ? `${template.projectType.color}15` : 'rgba(0, 212, 170, 0.1)',
                            display: 'flex'
                          }}>
                            <FileText size={16} color={template.projectType?.color || '#00d4aa'} />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {template.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {template.projectType && (
                          <Chip
                            label={template.projectType.name}
                            size="small"
                            sx={{
                              bgcolor: `${template.projectType.color}15`,
                              color: template.projectType.color,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {template.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{stats.totalSections}</TableCell>
                      <TableCell align="center">{stats.totalItems}</TableCell>
                      <TableCell align="center">{stats.totalDefaultHours}h</TableCell>
                      <TableCell align="center">{template.usageCount || 0}</TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={template.isActive}
                          onChange={() => handleToggleActive(template)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Renommer">
                            <IconButton size="small" onClick={() => handleOpenEditDialog(template)}>
                              <Pencil size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifier contenu">
                            <IconButton size="small" onClick={() => navigate(`/admin/templates/${template.id}`)}>
                              <Edit3 size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" onClick={() => handleDeleteTemplate(template.id)} sx={{ color: '#ef4444' }}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.01)',
            border: '1px dashed rgba(255, 255, 255, 0.1)',
            borderRadius: 2
          }}
        >
          <FileText size={48} color="#6b7280" style={{ marginBottom: 16 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {searchTerm || filterProjectType ? 'Aucun template trouvé' : 'Aucun template'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || filterProjectType
              ? 'Essayez avec d\'autres filtres de recherche'
              : 'Créez votre premier template pour accélérer la création de devis'
            }
          </Typography>
          {!searchTerm && !filterProjectType && (
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Créer un template
            </Button>
          )}
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Nouveau template</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Type de projet</InputLabel>
              <Select
                value={newTemplate.projectTypeId}
                onChange={(e) => setNewTemplate({ ...newTemplate, projectTypeId: e.target.value })}
                label="Type de projet"
              >
                {projectTypes.map(pt => (
                  <MenuItem key={pt.id} value={pt.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: pt.color 
                      }} />
                      <Box>
                        <Typography variant="body2">{pt.name}</Typography>
                        {pt.description && (
                          <Typography variant="caption" color="text.secondary">
                            {pt.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Nom du template"
              placeholder="Ex: Site vitrine standard, E-commerce complet..."
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              placeholder="Description du template..."
              value={newTemplate.description}
              onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateDialogOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTemplate}
            disabled={!newTemplate.name.trim() || !newTemplate.projectTypeId}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setEditingTemplate(null); }}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Modifier le template</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nom du template"
              value={editingTemplate?.name || ''}
              onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              placeholder="Description du template..."
              value={editingTemplate?.description || ''}
              onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setEditDialogOpen(false); setEditingTemplate(null); }} color="inherit">
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!editingTemplate?.name?.trim()}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TemplateManager;
